<script>
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, minLength, sameAs } from 'vuelidate/lib/validators'
import InfoHeader from '@/components/common/InfoHeader.vue'
import resetPasswordJson from '@/data/json/views/resetPassword.json'
import errorsJson from '@/data/json/errors/errors.json'
export default {
  components: {
    InfoHeader
  },
  mixins: [validationMixin],
  props: {
    // property from route, need to be string
    token: { type: String },
    userEmail: { type: String }
  },
  validations: {
    pwd: {
      required,
      minLength: minLength(6),
      hasCapital(pwd) {
        return /[A-Z]/.test(pwd)
      },
      hasNum(pwd) {
        return /[0-9]/.test(pwd)
      },
      hasSpecialChar(pwd) {
        return /\W|_/.test(pwd)
      }
    },
    repeatPwd: {
      required,
      sameAsPassword: sameAs('pwd'),
      minLength: minLength(6)
    }
  },
  data: () => ({
    content: resetPasswordJson.data.concat(errorsJson.data),
    pwd: '',
    repeatPwd: '',
    tokenExpired: false,
    passwordReseted: false,
    showPwd: false,
    showRepeatPwd: false
  }),
  computed: {
    ...mapGetters(['locale']),
    pwdErrors() {
      const errors = []
      if (!this.$v.pwd.$dirty) return errors
      !this.$v.pwd.required &&
      errors.push(this.$t('page.resetPassword.view.password.necessary'))
      !this.$v.pwd.minLength &&
      errors.push(this.$t('page.resetPassword.view.password.min.length'))
      !this.$v.pwd.hasCapital &&
      errors.push(this.$t('page.resetPassword.view.password.has.capital'))
      !this.$v.pwd.hasNum &&
      errors.push(this.$t('page.resetPassword.view.password.has.num'))
      !this.$v.pwd.hasSCha
      errors.push(this.$t('page.resetPassword.view.password.has.special.char'))
      return errors
    },
    repeatPwdErrors() {
      const errors = []
      if (!this.$v.repeatPwd.$dirty) return errors
      !this.$v.repeatPwd.required &&
      errors.push(
        this.$t('page.resetPassword.view.password.repetition.necessary')
      )
      !this.$v.repeatPwd.minLength &&
      errors.push(
        this.$t('page.resetPassword.view.password.repetition.min.length')
      )
      !this.$v.repeatPwd.sameAsPassword &&
      errors.push(this.$t('page.resetPassword.view.passwords.not.identical'))
      return errors
    }
  },
  methods: {
    submit() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        // formValidation failed
        this.$notify(this.$t('page.resetPassword.view.validator.message'), { type: 'error' })
        return
      }
      this.$userService
        .resetPassword({
          token: this.token,
          email: this.userEmail,
          newPassword: this.pwd,
          confirmPassword: this.repeatPwd
        })
        .then(response => {
          // request not success
          if (!response.succeeded) {
            this.$notify(this.$t('page.resetPassword.view.password.reset.failed'), {
              type: 'error'
            })
            // token expired ?
            if (
              response.errors.length > 0 &&
              response.errors[0].code === 'InvalidToken'
            ) {
              // show expired-content
              this.tokenExpired = true
            }
          } else {
            // show success-content
            this.passwordReseted = true
          }
        })
        .catch(e => {
          const msg = e.response?.data || this.$cms.get(this.content, e.response?.status)
          this.$notify(msg, { type: 'error' })
        })
    }
  }
}
</script>
<template name="password-reset-form">
  <v-container fluid class="pa-0">
    <InfoHeader />
    <v-card show max-width="500" color="white" class="mx-auto">
      <div v-if="tokenExpired">
        <DialogBaseColoredTitle color="primary">
          <h1 class="headline text-center">
            {{ $t('page.resetPassword.view.token.expired.header') }}
          </h1>
        </DialogBaseColoredTitle>
        <v-divider></v-divider>
        <DialogBaseText>
          <span class="body-2 cms_validatorMsg" v-html="$t('page.resetPassword.view.token.expired.content')"></span>
        </DialogBaseText>
        <v-divider />
        <DialogBaseActions>
          <v-btn
            class="primary"
            :to="{
              name: 'forgotPassword',
              params: { locale: locale }
            }"
          >
            {{ $t('button.request.new.password.change') }}
          </v-btn>
        </DialogBaseActions>
      </div>
      <div v-if="!tokenExpired && !passwordReseted">
        <form class="password-reset-form">
          <DialogBaseColoredTitle color="primary">
            <h1 class="headline text-center">
              {{ $t('page.resetPassword.view.header') }}
            </h1>
          </DialogBaseColoredTitle>
          <v-divider></v-divider>
          <DialogBaseText>
            <v-text-field
              id="password"
              v-model="pwd"
              :label="$t('page.resetPassword.view.new.password')"
              :error-messages="pwdErrors"
              :type="showPwd ? 'text' : 'password'"
              :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
              counter
              outlined
              dense
              required
              @input="$v.pwd.$touch()"
              @click:append="showPwd = !showPwd"
            ></v-text-field>
            <v-text-field
              id="repeatPassword"
              v-model="repeatPwd"
              :label="$t('page.resetPassword.view.new.password.repeat')"
              :error-messages="repeatPwdErrors"
              :type="showRepeatPwd ? 'text' : 'password'"
              :append-icon="showRepeatPwd ? 'mdi-eye' : 'mdi-eye-off'"
              counter
              outlined
              dense
              required
              @input="$v.repeatPwd.$touch()"
              @click:append="showRepeatPwd = !showRepeatPwd"
            ></v-text-field>
          </DialogBaseText>
          <v-divider />
          <DialogBaseActions>
            <v-btn
              id="passwordResetSubmit"
              class="text-center px-8"
              color="primary"
              dark
              depressed
              @click.stop="submit"
              >{{ $t('page.resetPassword.view.password.reset.label') }}</v-btn
            >
          </DialogBaseActions>
        </form>
      </div>
      <div v-if="passwordReseted">
        <DialogBaseColoredTitle color="primary">
          <h1 class="headline text-center">
            {{ $t('page.resetPassword.view.password.reset.successfull.header') }}
          </h1>
        </DialogBaseColoredTitle>
        <v-divider></v-divider>
        <DialogBaseText>
          <span class="body-2 cms_">
            {{ $t('page.resetPassword.view.password.reset.successfull.content') }}
          </span>
        </DialogBaseText>
        <v-divider />
        <DialogBaseActions>
          <v-btn
            :to="{
              name: 'login',
              params: { locale: locale }
            }"
            class="primary">
            {{ $t('button.go.to.login') }}
          </v-btn>
        </DialogBaseActions>
      </div>
    </v-card>
  </v-container>
</template>
